import { useState } from 'react'
import { Loader } from 'lucide-react'
import TemplateSelector from './TemplateSelector'
import QuestionnaireEditor from './QuestionnaireEditor'
import { getQuestionnaireByVersion } from '../../services/questionnaireService'

/**
 * QuestionnaireEditorFlow Component
 * Orchestrates the flow: template selection -> editor
 */
function QuestionnaireEditorFlow() {
  const [step, setStep] = useState('select') // 'select' | 'loading' | 'edit'
  const [templateQuestionnaire, setTemplateQuestionnaire] = useState(null)
  const [error, setError] = useState(null)

  const handleTemplateSelect = async (templateVersion) => {
    setError(null)
    
    // If null, start from scratch
    if (templateVersion === null) {
      setTemplateQuestionnaire(null)
      setStep('edit')
      return
    }
    
    // Fetch the template version
    setStep('loading')
    
    try {
      const response = await getQuestionnaireByVersion(templateVersion)
      
      // Transform the data to match editor format
      const sourceData = response.data.questionnaire
      
      // Transform themes and questions to editor format
      const transformedThemes = sourceData.themes.map(theme => ({
        id: theme.id,
        title: theme.title,
        icon: theme.icon || '📋',
        description: theme.description || '', // Add default description if missing
        questions: theme.questions.map(q => ({
          id: q.id,
          text: q.title || q.text || '', // Handle both 'title' and 'text' fields
          description: q.description || '', // Preserve question description (tooltip/help)
          weight: q.weight || (q.maxScore ? q.maxScore / 10 : 1) // Convert maxScore to weight
        }))
      }))
      
      const template = {
        title: sourceData.title,
        description: sourceData.description,
        themes: transformedThemes
      }
      
      console.log('Transformed template:', template)
      setTemplateQuestionnaire(template)
      setStep('edit')
      
    } catch (err) {
      console.error('Error loading template:', err)
      setError(err.message || 'Erreur lors du chargement du template')
      setStep('select')
    }
  }

  const handleEditorSuccess = () => {
    // Reset to template selection for next creation
    setStep('select')
    setTemplateQuestionnaire(null)
  }

  const handleCancel = () => {
    setStep('select')
    setTemplateQuestionnaire(null)
    setError(null)
  }

  // Step 1: Template Selection
  if (step === 'select') {
    return (
      <div>
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}
        <TemplateSelector 
          onSelect={handleTemplateSelect}
          onCancel={null} // No cancel button on initial screen
        />
      </div>
    )
  }

  // Step 2: Loading Template
  if (step === 'loading') {
    return (
      <div className="flex items-center justify-center py-24 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement du template...</p>
        </div>
      </div>
    )
  }

  // Step 3: Editor
  if (step === 'edit') {
    return (
      <div>
        <div className="mb-4">
          <button
            onClick={handleCancel}
            className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour à la sélection
          </button>
        </div>
        <QuestionnaireEditor 
          initialQuestionnaire={templateQuestionnaire}
          onSuccess={handleEditorSuccess}
        />
      </div>
    )
  }

  return null
}

export default QuestionnaireEditorFlow

